// HOME page — greeting/quote, reminders, КБЖУ summary, workout status, weight +
// projection + chart, tomorrow preview. Ported from the original app's HOME section.
// Water widget dropped entirely (feature removed). AI-chat teaser dropped.
import Chart from 'chart.js/auto';
import { S, MEALS, MEAL_LABELS } from '../state.js';
import { save } from '../lib/sync.js';
import { today, fmtDate, fmtDateShort, shiftDate } from '../lib/dates.js';
import { n0, n1, escHtml } from '../lib/format.js';
import { dayKbzhu, kbzhuBar, wkStatusLine, calcProjection, calcNextWeighForecast, calibrateMaintenanceCalories, isRestDay } from '../lib/calc.js';
import { IC, cicon } from '../lib/icons.js';
import { openModal, closeModal } from '../lib/modal.js';
import { showConfirm } from '../components/confirmDialog.js';
import { openValuePrompt } from '../components/valuePrompt.js';
import { registerPage, goNav } from '../lib/router.js';
import { checkSyncNow } from '../lib/sync.js';

let weightChart = null;

function greetPeriod() {
  const hr = new Date().getHours();
  return hr < 12 ? 'morning' : hr < 18 ? 'day' : 'evening';
}

function pickGreeting() {
  const period = greetPeriod();
  const base = { morning: 'Доброе утро', day: 'Добрый день', evening: 'Добрый вечер' }[period];
  const icon = { morning: 'sun', day: 'sun', evening: 'moon' }[period];
  const name = S.settings.userName ? ', ' + S.settings.userName : '';
  return `${base}${name} ${cicon(icon, 16)}`;
}

// Custom quotes only — the app never falls back to a built-in pool, matching the
// original's explicit choice ("empty custom base — show nothing").
function pickQuote() {
  const period = 'day'; // site always draws from the general pool
  const pool = (S.customQuotes && S.customQuotes[period]) || [];
  if (!pool.length) return '';
  const QUOTE_STABLE_MS = 30 * 60 * 1000;
  let saved = null;
  try { saved = JSON.parse(localStorage.getItem('levelup_quote') || 'null'); } catch (e) {}
  if (saved && saved.period === period && (Date.now() - saved.at) < QUOTE_STABLE_MS) {
    return saved.text;
  }
  const bagKey = 'levelup_quoteBag_' + period;
  let bagData = null;
  try { bagData = JSON.parse(localStorage.getItem(bagKey) || 'null'); } catch (e) {}
  const poolSig = pool.length + ':' + pool[0];
  let bag = (bagData && bagData.sig === poolSig) ? bagData.items : null;
  if (!bag || !bag.length) {
    bag = pool.slice();
    for (let i = bag.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [bag[i], bag[j]] = [bag[j], bag[i]];
    }
  }
  const pick = bag.pop();
  try { localStorage.setItem(bagKey, JSON.stringify({ sig: poolSig, items: bag })); } catch (e) {}
  try { localStorage.setItem('levelup_quote', JSON.stringify({ text: pick, at: Date.now(), period })); } catch (e) {}
  return pick;
}

// Closest logged weight on or before `dateStr` — falls back to the declared start weight
// if nothing was logged yet by then (that's the honest baseline before any entries exist).
export function weightOnOrBefore(dateStr) {
  const wlog = [...S.weightLog].filter(w => w.date <= dateStr).sort((a, b) => a.date.localeCompare(b.date));
  return wlog.length ? wlog[wlog.length - 1].w : S.startWeight;
}

// Small eye icon that toggles one specific piece of the weight dashboard, independently
// of the others and independently of the chart/archive-wide toggle (S.settings.hideWeight,
// see wEyeBtn in index.html) — per explicit request for per-element control "значком".
function eyeBtn(key, isHidden) {
  return `<button class="btn-icon-toggle" onclick="toggleWeightPart('${key}')" style="background:none;border:none;cursor:pointer;padding:0;color:var(--text3);flex-shrink:0" title="Скрыть/показать">${isHidden ? IC.eyeOff : IC.eye}</button>`;
}

function renderWeightSection() {
  const wlog = [...S.weightLog].sort((a, b) => a.date.localeCompare(b.date));
  const lastW = wlog.length ? wlog[wlog.length - 1] : null;
  const curW = lastW ? lastW.w : S.startWeight;
  const prevW = wlog.length > 1 ? wlog[wlog.length - 2].w : S.startWeight;
  const diff = n1(curW - prevW);
  const show = S.settings.weightShow || { current: true, diff: true, stat: true, progress: true, nextForecast: true };

  const totalLost = n1(S.startWeight - curW);
  const weekLost = n1(weightOnOrBefore(shiftDate(today(), -7)) - curW);
  const mode = S.settings.weightStatMode || 'toGoal';
  const statLabel = { toGoal: 'До цели', week: 'За неделю', total: 'Всего сброшено' }[mode];
  let statText;
  if (mode === 'week') statText = weekLost + ' кг';
  else if (mode === 'total') statText = totalLost + ' кг';
  else { const toGoal = n1(curW - S.goalWeight); statText = toGoal > 0 ? toGoal + ' кг' : 'Цель достигнута!'; }

  const diffText = diff === 0 ? 'Без изменений' : (diff > 0 ? '▲ +' + diff + ' кг' : '▼ ' + diff + ' кг');
  const diffColor = diff > 0 ? 'var(--red)' : diff < 0 ? 'var(--accent)' : 'var(--text3)';
  const editRow = wlog.length ? `<div style="margin-top:6px;display:flex;gap:8px;justify-content:center">
      <button onclick="editLastWeight()" style="background:none;border:none;color:var(--text3);font-size:11px;cursor:pointer;padding:0;text-decoration:underline">Изменить</button>
      <button onclick="deleteLastWeight()" style="background:none;border:none;color:var(--text3);font-size:11px;cursor:pointer;padding:0;text-decoration:underline">Удалить</button>
    </div>` : '';

  document.getElementById('hWeightBlock').innerHTML = `
    <div class="dash-2col">
      <div class="dash-half" style="text-align:center">
        <div style="display:flex;justify-content:space-between;align-items:center">
          <span class="dash-half-label" style="margin-bottom:0">Текущий</span>
          ${eyeBtn('current', !show.current)}
        </div>
        <div class="dash-big t-num">${show.current ? curW + ' кг' : '••••'}</div>
        <div style="display:flex;justify-content:center;align-items:center;gap:6px;margin-top:2px;min-height:14px">
          <span class="dash-sm" style="color:${diffColor}">${show.diff ? diffText : ''}</span>
          ${eyeBtn('diff', !show.diff)}
        </div>
        ${editRow}
      </div>
      <div class="dash-half" style="text-align:center;display:flex;flex-direction:column">
        <div style="display:flex;justify-content:space-between;align-items:center">
          <span class="dash-half-label" style="margin-bottom:0">${statLabel}</span>
          ${eyeBtn('stat', !show.stat)}
        </div>
        <div style="flex:1;display:flex;flex-direction:column;justify-content:center">
          <div class="dash-big t-num t-accent">${show.stat ? statText : '••••'}</div>
        </div>
      </div>
    </div>`;

  // % of the way from start to goal, plus total lost so far — independent visibility too.
  const progEl = document.getElementById('hWeightProgress');
  if (!show.progress) {
    progEl.innerHTML = `<div style="display:flex;justify-content:flex-end">${eyeBtn('progress', true)}</div>`;
  } else if (S.startWeight === S.goalWeight) {
    progEl.innerHTML = '';
  } else {
    const pct = Math.max(0, Math.min(100, Math.round(((S.startWeight - curW) / (S.startWeight - S.goalWeight)) * 100)));
    progEl.innerHTML = `<div style="display:flex;justify-content:space-between;align-items:center;gap:8px">
        <span class="dash-sm" style="flex:1;min-width:0">Сброшено всего: ${totalLost} кг · Прогресс к цели: ${pct}%</span>
        ${eyeBtn('progress', false)}
      </div>
      <div class="ptrack" style="margin-top:5px"><div class="pfill pf-green" style="width:${pct}%"></div></div>`;
  }
}

export function toggleWeightPart(key) {
  if (!S.settings.weightShow) S.settings.weightShow = { current: true, diff: true, stat: true, progress: true, nextForecast: true };
  S.settings.weightShow[key] = !S.settings.weightShow[key];
  save(true);
  if (key === 'nextForecast') renderNextWeighForecast();
  else renderWeightSection();
}

export function calcWeightRange() {
  const from = document.getElementById('hRangeFrom').value;
  const to = document.getElementById('hRangeTo').value;
  const el = document.getElementById('hRangeResult');
  if (!from || !to) { el.textContent = ''; return; }
  const wFrom = weightOnOrBefore(from);
  const wTo = weightOnOrBefore(to);
  const diff = n1(wFrom - wTo);
  if (diff > 0) el.textContent = `Сброшено ${diff} кг (${wFrom} → ${wTo} кг)`;
  else if (diff < 0) el.textContent = `Набрано ${Math.abs(diff)} кг (${wFrom} → ${wTo} кг)`;
  else el.textContent = `Без изменений (${wFrom} кг)`;
}

function renderReminders() {
  const el = document.getElementById('hReminders');
  const nowH = new Date().getHours(), nowM = new Date().getMinutes();
  const nowMinutes = nowH * 60 + nowM;
  const td = today();
  let cards = [];

  const MEAL_DEADLINE = { breakfast: 11 * 60, lunch: 15 * 60, dinner: 21 * 60, snacks: 22 * 60 };
  const flToday = S.foodLog[td] || {};
  MEALS.forEach(m => {
    const pending = (flToday[m] || []).filter(it => it.eaten === false);
    if (pending.length && nowMinutes >= MEAL_DEADLINE[m]) {
      cards.push(`<div class="reminder-card">
        <span class="reminder-icon">${cicon('meal', 18)}</span>
        <span>Не отмечено: ${pending.map(it => escHtml(it.name)).join(', ')} (${MEAL_LABELS[m].toLowerCase()})</span>
        <button class="reminder-action btn btn-sm btn-secondary" onclick="setFoodDate('${td}');goNav('food')">Открыть</button>
      </div>`);
    }
  });

  if (S.vitConfig && S.vitConfig.slots) {
    const lowItems = [];
    S.vitConfig.slots.forEach(slot => {
      (slot.items || []).forEach(item => {
        if (item.stock !== undefined && item.stock !== null && item.stock <= 7) lowItems.push(item);
      });
    });
    if (lowItems.length) {
      cards.push(`<div class="reminder-card">
        <span class="reminder-icon">${cicon('pill', 18)}</span>
        <span>Заканчивается: ${lowItems.map(i => escHtml(i.name) + ' (' + i.stock + ' шт)').join(', ')} — пора докупить</span>
        <button class="reminder-action btn btn-sm btn-secondary" onclick="setVitDate('${td}');goNav('vitamins')">Открыть</button>
      </div>`);
    }
  }

  const cfg = S.vitConfig;
  if (cfg && cfg.slots && cfg.slots.length) {
    const vitToday = (S.vitamins && S.vitamins[td]) || {};
    // Mirror the same rest-day visibility rule as the Vitamins page itself (see
    // pages/vitamins.js renderVitamins) — otherwise this nagged about a slot the Vitamins
    // page was already showing as "Скрыто — выходной день".
    const hideForRest = isRestDay(td) && S.settings.showVitOnRest !== true;
    const pending = cfg.slots
      .map((slot, si) => ({ slot, si }))
      .filter(({ slot }) => slot.time && slot.items && slot.items.length)
      .filter(({ slot }) => !(hideForRest && slot.items.every(it => it.skipOnRest))) // fully hidden today
      .filter(({ slot, si }) => {
        const visible = slot.items.map((it, ii) => ({ it, ii })).filter(({ it }) => !(hideForRest && it.skipOnRest));
        return !visible.every(({ ii }) => vitToday[si + '_' + ii]);
      })
      .map(({ slot, si }) => {
        const [th, tm] = slot.time.split(':').map(Number);
        return { slot, si, minutes: th * 60 + tm };
      });

    const overdueOnes = pending.filter(p => nowMinutes >= p.minutes + 15)
      .sort((a, b) => (nowMinutes - a.minutes) - (nowMinutes - b.minutes));
    const upcomingOnes = pending.filter(p => nowMinutes < p.minutes + 15)
      .sort((a, b) => a.minutes - b.minutes);

    let chosen = null, overdue = false;
    if (overdueOnes.length) {
      chosen = overdueOnes[0];
      overdue = true;
    } else if (upcomingOnes.length && upcomingOnes[0].minutes - nowMinutes <= 120) {
      chosen = upcomingOnes[0];
    }

    if (chosen) {
      cards.push(`<div class="reminder-card">
        <span class="reminder-icon">${cicon('pill', 18)}</span>
        <span>${overdue ? 'Пора выпить' : 'Не забудь'} витамины «${escHtml(chosen.slot.name)}» (${chosen.slot.time})</span>
        <button class="reminder-action btn btn-sm btn-secondary" onclick="setVitDate('${td}');goNav('vitamins')">Открыть</button>
      </div>`);
    }
  }

  const wToday = S.workoutLog.find(w => w.date === td);
  if (nowMinutes >= 17 * 60 && wToday && !wToday.rest && (wToday.cardio || wToday.strength) && !wToday.kcalBurned && !wToday.duration) {
    cards.push(`<div class="reminder-card">
      <span class="reminder-icon">${cicon('dumbbell', 18)}</span>
      <span>Внёс тренировку сегодня — не забудь дозаполнить калории/время</span>
      <button class="reminder-action btn btn-sm btn-secondary" onclick="setWkDate('${td}');goNav('workout')">Открыть</button>
    </div>`);
  }

  if (nowMinutes >= 22 * 60 && wToday && !wToday.rest && !wToday.steps) {
    cards.push(`<div class="reminder-card">
      <span class="reminder-icon">${cicon('shoe', 18)}</span>
      <span>Ещё не внёс шаги за сегодня</span>
      <button class="reminder-action btn btn-sm btn-secondary" onclick="setWkDate('${td}');goNav('workout')">Открыть</button>
    </div>`);
  }

  const html = cards.join('');
  if (el.dataset.lastHtml !== html) { // avoid needless DOM churn when nothing actually changed
    el.innerHTML = html;
    el.dataset.lastHtml = html;
  }
}

function renderWkStatus() {
  const td = today();
  const tom = shiftDate(td, 1);
  const wToday = S.workoutLog.find(w => w.date === td);
  const wTom = S.workoutLog.find(w => w.date === tom);
  const st = wkStatusLine(wToday);
  document.getElementById('hWkToday').innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:center">
      <div><div class="t-caption">Сегодня</div><div class="t-h3" style="margin-top:2px">${st.icon} ${st.text}</div></div>
      <button class="btn btn-sm btn-secondary" onclick="goNav('workout')">Открыть</button>
    </div>`;
  const planTom = !wTom && S.weeklyPlan?.[tom];
  const stTom = wkStatusLine(wTom || planTom || null);
  document.getElementById('hWkTomorrow').innerHTML = `Завтра: ${stTom.icon} ${stTom.text}${planTom ? ' <span class="tag tag-neutral">план</span>' : ''}`;
}

function renderTomorrow() {
  const tom = shiftDate(today(), 1);
  const fl = S.foodLog[tom] || {};
  const hasFood = Object.values(fl).some(arr => arr.length > 0);
  const sec = document.getElementById('tomorrowSection');
  const el = document.getElementById('tomorrowCard');
  if (!hasFood) { sec.style.display = 'none'; return; }
  sec.style.display = '';
  const kbzhu = dayKbzhu(tom);
  let html = `<div style="margin-bottom:6px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:var(--text3)">${fmtDate(tom)}</div>`;
  html += `<div style="margin-bottom:8px"><span class="tag tag-amber">${kbzhu.k} ккал</span> <span style="font-size:11px;color:var(--text3)">Б ${kbzhu.p} · Ж ${kbzhu.f} · У ${kbzhu.c}</span></div>`;
  MEALS.forEach(m => {
    const items = (fl[m] || []);
    if (!items.length) return;
    html += `<div style="margin-bottom:4px"><span style="font-size:11px;font-weight:700;color:var(--text3)">${MEAL_LABELS[m]}:</span> `;
    html += items.map(it => `${escHtml(it.name)} ${it.g || 0}г`).join(', ') + '</div>';
  });
  el.innerHTML = html;
}

function renderWeightChart(wlog) {
  const canvas = document.getElementById('weightChart');
  const ctx = canvas.getContext('2d');
  if (weightChart) weightChart.destroy();
  const hidden = !!S.settings.hideWeight;
  const dark = S.settings.theme === 'dark';
  const gridColor = dark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.04)';
  const tickColor = dark ? '#75736E' : undefined;
  const labels = wlog.slice(-12).map(w => fmtDateShort(w.date));
  const data = wlog.slice(-12).map(w => w.w);
  weightChart = new Chart(ctx, {
    type: 'line',
    data: { labels, datasets: [{
      data, borderColor: '#1D9B5F', borderWidth: 2, pointBackgroundColor: '#1D9B5F',
      pointRadius: 3, tension: 0.3, fill: { target: 'origin', above: 'rgba(29,155,95,.06)' },
    }] },
    options: {
      responsive: true, maintainAspectRatio: false, // container has a fixed CSS height — see .card wrapper
      plugins: { legend: { display: false }, tooltip: { enabled: !hidden, callbacks: { label: c => c.raw + ' кг' } } },
      scales: {
        x: { grid: { display: false }, ticks: { font: { size: 9 }, maxRotation: 0, color: tickColor } },
        y: { grid: { color: gridColor }, ticks: { display: !hidden, font: { size: 9 }, callback: v => v + ' кг', color: tickColor }, min: data.length ? Math.min(...data) - 2 : undefined },
      },
    },
  });
}

export function renderHome() {
  const td = today();
  document.getElementById('homeDateLabel').textContent = fmtDate(td);
  document.getElementById('homeGreeting').innerHTML = pickGreeting();
  const quoteText = pickQuote();
  document.getElementById('homeQuote').textContent = quoteText;
  document.getElementById('homeQuoteCard').style.display = quoteText ? '' : 'none';

  renderReminders();
  updateWeightEyeBtn();
  renderWeightSection();

  const kbzhu = dayKbzhu(td);
  document.getElementById('hK').textContent = kbzhu.k;
  document.getElementById('hP').textContent = kbzhu.p;
  document.getElementById('hF').textContent = kbzhu.f;
  document.getElementById('hC').textContent = kbzhu.c;
  document.getElementById('hKbzhuBars').innerHTML = kbzhuBar(kbzhu.k, kbzhu.p, kbzhu.f, kbzhu.c);
  const warn = document.getElementById('homeKbzhuWarn');
  if (kbzhu.k > S.settings.goalKcal) {
    warn.textContent = 'Превышение калорий: ' + kbzhu.k + ' / ' + S.settings.goalKcal + ' ккал';
    warn.className = 'warn-alert over visible';
  } else if (kbzhu.k > S.settings.goalKcal * 0.8) {
    warn.textContent = 'Близко к лимиту: ' + kbzhu.k + ' / ' + S.settings.goalKcal + ' ккал';
    warn.className = 'warn-alert visible';
  } else { warn.className = 'warn-alert'; }

  renderWkStatus();

  const wlog = [...S.weightLog].sort((a, b) => a.date.localeCompare(b.date));
  document.getElementById('hProjection').textContent = calcProjection(wlog);
  renderNextWeighForecast(wlog);
  const tdeeRow = document.getElementById('hTdeeRow');
  const tdee = calibrateMaintenanceCalories();
  if (tdee != null) {
    tdeeRow.style.display = 'block';
    tdeeRow.textContent = `Прогноз учитывает рацион и тренировки — твоя норма по факту ≈ ${n0(tdee)} ккал/день`;
  } else {
    tdeeRow.style.display = 'none';
  }
  renderWeightChart(wlog);
  renderTomorrow();
}
registerPage('home', renderHome);

// Predicted weight at the next weigh-in — hideable independently (per-weigh-in privacy),
// but hiding it still shows the expected CHANGE (e.g. "▼ -0.3 кг") rather than nothing, since
// that's the part actually useful for staying motivated without seeing the absolute number.
function renderNextWeighForecast(wlog) {
  wlog = wlog || [...S.weightLog].sort((a, b) => a.date.localeCompare(b.date));
  const show = S.settings.weightShow || { nextForecast: true };
  const nextRow = document.getElementById('hNextWeighRow');
  const forecast = calcNextWeighForecast(wlog);
  if (!forecast) { nextRow.style.display = 'none'; return; }
  nextRow.style.display = 'block';
  const last = wlog[wlog.length - 1];
  const delta = n1(forecast.weight - last.w);
  const deltaText = delta === 0 ? 'Без изменений' : (delta > 0 ? '▲ +' + delta + ' кг' : '▼ ' + delta + ' кг');
  const valueText = show.nextForecast ? `≈ ${forecast.weight} кг (${forecast.date})` : `${deltaText} (${forecast.date})`;
  nextRow.innerHTML = `<div style="display:flex;justify-content:space-between;align-items:center;gap:8px">
      <span class="t-secondary">Прогноз на след. взвешивание</span>
      <div style="display:flex;align-items:center;gap:4px;flex-shrink:0">
        <span class="t-h3">${valueText}</span>
        ${eyeBtn('nextForecast', !show.nextForecast)}
      </div>
    </div>`;
}

// Controls the weight CHART's numbers/tooltip and the Archive weight list only — the
// dashboard cells on Home have their own independent toggles now, see toggleWeightPart().
export function toggleWeightVisibility() {
  S.settings.hideWeight = !S.settings.hideWeight;
  save(true);
  updateWeightEyeBtn();
  const wlog = [...S.weightLog].sort((a, b) => a.date.localeCompare(b.date));
  renderWeightChart(wlog);
}

function updateWeightEyeBtn() {
  const btn = document.getElementById('wEyeBtn');
  if (btn) btn.innerHTML = S.settings.hideWeight ? IC.eyeOff : IC.eye;
}

export function openLogWeight() {
  document.getElementById('lwInp').value = '';
  openModal('shLogWeight');
  setTimeout(() => document.getElementById('lwInp').focus(), 200);
}

export function saveLogWeight() {
  const raw = document.getElementById('lwInp').value;
  const v = parseFloat(String(raw).replace(',', '.'));
  const inp = document.getElementById('lwInp');
  if (isNaN(v) || v < 30 || v > 300) { inp.style.borderColor = 'var(--red)'; inp.placeholder = 'Введи корректный вес (30–300)'; return; }
  const td = today();
  S.weightLog = S.weightLog.filter(w => w.date !== td);
  S.weightLog.push({ date: td, w: v });
  S.weightLog.sort((a, b) => a.date.localeCompare(b.date));
  save(true);
  closeModal('shLogWeight');
  renderHome();
}

// Quick fix for the most recently logged weight entry — no password needed, this is for
// correcting a value just typed, not editing history (that's Archive, password-gated).
export function editLastWeight() {
  const wlog = [...S.weightLog].sort((a, b) => a.date.localeCompare(b.date));
  if (!wlog.length) return;
  const last = wlog[wlog.length - 1];
  openValuePrompt('Новое значение веса (кг) для ' + fmtDateShort(last.date), last.w, nv => {
    if (isNaN(nv) || nv < 30 || nv > 300) return;
    const idx = S.weightLog.findIndex(w => w.date === last.date);
    if (idx >= 0) S.weightLog[idx].w = nv;
    save(true);
    renderHome();
  });
}

export function deleteLastWeight() {
  const wlog = [...S.weightLog].sort((a, b) => a.date.localeCompare(b.date));
  if (!wlog.length) return;
  const last = wlog[wlog.length - 1];
  showConfirm('Удалить запись веса за ' + fmtDateShort(last.date) + ' (' + last.w + ' кг)?', () => {
    const idx = S.weightLog.findIndex(w => w.date === last.date);
    if (idx >= 0) S.weightLog.splice(idx, 1);
    save(true);
    renderHome();
  });
}

export { checkSyncNow };
